/**
 * Simulation route — exam-style session for einbuergerungstest.
 *
 * Flow:
 *   1. On mount, check /simulation/in-progress.
 *      - If a sim is in progress → prompt "Resume?" (resume uses the same
 *        question manifest; local-storage-backed answers are restored).
 *      - Else → POST /simulation/start with the configured preset.
 *   2. Walk the cards one at a time. NO per-card feedback (that's the point).
 *      Each answer is captured in local state + mirrored to localStorage so
 *      a browser refresh doesn't lose progress.
 *   3. On the last card the "Submit test" button replaces "Next" — the user
 *      reviews their answer one more time before scoring. Submit calls
 *      /simulation/:id/submit, clears local storage, and navigates to the
 *      result page with the response in route state.
 *   4. Quit triggers a confirm dialog → DELETE /simulation/:id, clear local
 *      storage, back to app home.
 *
 * Note: AppHome only links here for einbuergerungstest, but the route is
 * parameterised by appId so future apps with a simulation mode reuse it.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

import { CardFront } from '@/apps/einbuergerungstest/CardFront';
import { SimulationShell } from '@/components/simulation/SimulationShell';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import type { Card, EinbuergerungsPayload, OptionLetter } from '@/core/types';
import {
  api,
  ApiError,
  type SimulationStartResponse,
  type SimulationSubmitResponse,
} from '@/lib/api';

type AnswerMap = Record<number, OptionLetter | null>;

/** Local-only state cached in localStorage for browser-refresh resume. */
type SimLocalState = {
  answers: AnswerMap;
  bookmarked_during_sim: number[]; // card ids the user marked during this sim
};

const EMPTY_STATE: SimLocalState = { answers: {}, bookmarked_during_sim: [] };

function storageKey(simId: number) {
  return `eb-sim-answers-${simId}`;
}

function loadState(simId: number): SimLocalState {
  try {
    const raw = localStorage.getItem(storageKey(simId));
    if (!raw) return EMPTY_STATE;
    const parsed = JSON.parse(raw) as Partial<SimLocalState>;
    if (!parsed || typeof parsed !== 'object') return EMPTY_STATE;
    return {
      answers: parsed.answers && typeof parsed.answers === 'object' ? parsed.answers : {},
      bookmarked_during_sim: Array.isArray(parsed.bookmarked_during_sim)
        ? parsed.bookmarked_during_sim
        : [],
    };
  } catch {
    return EMPTY_STATE;
  }
}

function saveState(simId: number, state: SimLocalState) {
  try {
    localStorage.setItem(storageKey(simId), JSON.stringify(state));
  } catch {
    // localStorage may be unavailable (private mode, quota). Best-effort only.
  }
}

function clearState(simId: number) {
  try {
    localStorage.removeItem(storageKey(simId));
  } catch {
    // ignore
  }
}

export function SimulationRoute() {
  const { appId } = useParams<{ appId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Simulation state. `null` while booting or resolving an in-progress check.
  const [sim, setSim] = useState<SimulationStartResponse | null>(null);
  const [bootError, setBootError] = useState<string | null>(null);
  const [resumePrompt, setResumePrompt] = useState<SimulationStartResponse | null>(null);
  const [answers, setAnswers] = useState<AnswerMap>({});
  // Card IDs the user marked during the sim. Deferred to submit so abandoning
  // doesn't leak bookmarks into the persistent flag.
  const [bookmarkedDuringSim, setBookmarkedDuringSim] = useState<Set<number>>(new Set());
  const [cardIdx, setCardIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [quitOpen, setQuitOpen] = useState(false);
  const [confirmSubmitOpen, setConfirmSubmitOpen] = useState(false);

  // Boot: check for in-progress sim, otherwise prepare to start a fresh one.
  useEffect(() => {
    let cancelled = false;
    api.einbuergerungstest
      .getInProgress()
      .then((res) => {
        if (cancelled) return;
        if (res.in_progress) {
          setResumePrompt(res.in_progress);
        } else {
          startFreshSimulation();
        }
      })
      .catch((err: ApiError) => {
        if (!cancelled) setBootError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const startFreshSimulation = async () => {
    try {
      const fresh = await api.einbuergerungstest.startSimulation();
      setSim(fresh);
      setAnswers({});
      setBookmarkedDuringSim(new Set());
      setCardIdx(0);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : String(err);
      setBootError(msg);
    }
  };

  const resumeSimulation = () => {
    if (!resumePrompt) return;
    const restored = loadState(resumePrompt.sim_id);
    setSim(resumePrompt);
    setAnswers(restored.answers);
    setBookmarkedDuringSim(new Set(restored.bookmarked_during_sim));
    // Position at the first unanswered card if any answers exist, else 0.
    const firstUnansweredIdx = resumePrompt.cards.findIndex(
      (c) => restored.answers[c.id] === undefined,
    );
    setCardIdx(firstUnansweredIdx === -1 ? 0 : firstUnansweredIdx);
    setResumePrompt(null);
  };

  const abandonAndStartNew = async () => {
    if (!resumePrompt) return;
    try {
      await api.einbuergerungstest.abandonSimulation(resumePrompt.sim_id);
    } catch {
      // Non-fatal — if delete failed, /start will 409. We let the user see it.
    }
    clearState(resumePrompt.sim_id);
    setResumePrompt(null);
    startFreshSimulation();
  };

  // ---- Resume dialog ----
  if (resumePrompt) {
    return (
      <SimulationShell
        onQuit={() => navigate(`/apps/${appId}`)}
        current={0}
        total={resumePrompt.total_questions}
        passThreshold={resumePrompt.pass_threshold}
      >
        <div className="border-border bg-card flex flex-col items-center gap-4 rounded-2xl border p-8 text-center shadow-sm">
          <h2 className="font-display text-2xl font-semibold">
            You have a simulation in progress.
          </h2>
          <p className="text-muted-foreground text-sm">
            Started {new Date(resumePrompt.started_at ?? Date.now()).toLocaleString()}.
            <br />
            Pick up where you left off, or start a fresh test.
          </p>
          <div className="mt-2 flex flex-wrap justify-center gap-3">
            <Button size="lg" onClick={resumeSimulation}>
              Resume
            </Button>
            <Button size="lg" variant="outline" onClick={abandonAndStartNew}>
              Start fresh
            </Button>
          </div>
        </div>
      </SimulationShell>
    );
  }

  // ---- Boot error ----
  if (bootError) {
    return (
      <SimulationShell onQuit={() => navigate(`/apps/${appId}`)} current={0} total={0}>
        <div className="border-destructive/40 bg-destructive/5 text-destructive flex flex-col items-center gap-3 rounded-2xl border p-6 text-center">
          <p>{bootError}</p>
          <Button variant="outline" onClick={() => navigate(`/apps/${appId}`)}>
            Back to home
          </Button>
        </div>
      </SimulationShell>
    );
  }

  // ---- Loading ----
  if (!sim) {
    return (
      <SimulationShell onQuit={() => navigate(`/apps/${appId}`)} current={0} total={0}>
        <p className="text-muted-foreground text-center">Preparing your test…</p>
      </SimulationShell>
    );
  }

  const total = sim.cards.length;
  const currentCard = sim.cards[cardIdx];
  const isLastCard = cardIdx === total - 1;

  const persistLocalState = (nextAnswers: AnswerMap, nextBookmarks: Set<number>) => {
    saveState(sim.sim_id, {
      answers: nextAnswers,
      bookmarked_during_sim: Array.from(nextBookmarks),
    });
  };

  const handleAnswer = (event: { user_answer: string | null }) => {
    const letter = event.user_answer as OptionLetter | null;
    const next: AnswerMap = { ...answers, [currentCard.id]: letter };
    setAnswers(next);
    persistLocalState(next, bookmarkedDuringSim);
    // On the last card, do NOT auto-advance — the user reviews and clicks
    // "Submit test" explicitly. On other cards, advance to the next card.
    if (!isLastCard) {
      setCardIdx((idx) => idx + 1);
    }
  };

  const handleBookmarkToggle = (cardId: number) => {
    const next = new Set(bookmarkedDuringSim);
    if (next.has(cardId)) next.delete(cardId);
    else next.add(cardId);
    setBookmarkedDuringSim(next);
    persistLocalState(answers, next);
  };

  const submit = async () => {
    setSubmitting(true);
    setConfirmSubmitOpen(false);
    try {
      const payload = sim.cards.map((c) => ({
        card_id: c.id,
        user_letter: answers[c.id] ?? null,
        bookmarked_during_sim: bookmarkedDuringSim.has(c.id) || undefined,
      }));
      const result = await api.einbuergerungstest.submitSimulation(sim.sim_id, payload);
      clearState(sim.sim_id);
      await queryClient.invalidateQueries({ queryKey: ['apps'] });
      await queryClient.invalidateQueries({ queryKey: ['streak'] });
      await queryClient.invalidateQueries({ queryKey: ['stats'] });
      await queryClient.invalidateQueries({ queryKey: ['einbuergerungstest'] });
      navigate(`/apps/${appId}/simulation/result`, {
        state: { result } satisfies SimulationResultLocationState,
        replace: true,
      });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : String(err);
      toast.error('Failed to submit', { description: msg });
      setSubmitting(false);
    }
  };

  const onConfirmQuit = async () => {
    setQuitOpen(false);
    try {
      await api.einbuergerungstest.abandonSimulation(sim.sim_id);
    } catch {
      // ignore — best effort
    }
    clearState(sim.sim_id);
    navigate(`/apps/${appId}`);
  };

  return (
    <>
      <SimulationShell
        onQuit={() => setQuitOpen(true)}
        current={cardIdx + 1}
        total={total}
        passThreshold={sim.pass_threshold}
      >
        <CardFront
          card={currentCard as unknown as Card<EinbuergerungsPayload>}
          onAnswer={(e) => handleAnswer(e)}
          bookmarkOverride={{
            // Reflect either the card's persistent bookmark OR the user's
            // intent during this sim, so the icon doesn't "un-fill" mid-sim.
            bookmarked:
              !!(currentCard as unknown as Card<EinbuergerungsPayload>).bookmarked ||
              bookmarkedDuringSim.has(currentCard.id),
            onToggle: () => handleBookmarkToggle(currentCard.id),
          }}
        />

        {isLastCard ? (
          <div className="border-primary/30 bg-primary/5 mt-6 flex flex-col items-center gap-3 rounded-xl border p-4 text-center">
            <p className="text-muted-foreground text-sm">
              Last question. Review your answer above, then submit to see the result.
            </p>
            <Button
              size="lg"
              disabled={submitting || answers[currentCard.id] === undefined}
              onClick={() => setConfirmSubmitOpen(true)}
            >
              {submitting ? 'Scoring…' : 'Submit test'}
            </Button>
          </div>
        ) : null}
      </SimulationShell>

      <ConfirmDialog
        open={quitOpen}
        onOpenChange={setQuitOpen}
        title="Quit this simulation?"
        description="Your answers will be discarded and this simulation will be marked abandoned."
        confirmLabel="Quit simulation"
        cancelLabel="Keep going"
        destructive
        onConfirm={onConfirmQuit}
      />

      <ConfirmDialog
        open={confirmSubmitOpen}
        onOpenChange={setConfirmSubmitOpen}
        title="Submit your test?"
        description={
          Object.keys(answers).length < sim.cards.length
            ? `You have ${sim.cards.length - Object.keys(answers).length} unanswered question(s). They'll count as incorrect.`
            : 'You can review the result on the next screen.'
        }
        confirmLabel="Submit"
        onConfirm={submit}
      />
    </>
  );
}

// Shared between simulation.tsx and simulation-result.tsx so the result page
// can type-assert the route state.
export type SimulationResultLocationState = {
  result: SimulationSubmitResponse;
};
